import { useState, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faDollarSign,
  faCalendarAlt,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/ui/modal";
import { toastError, toastSuccess } from "../../../utils/toast";

const EditCapital = ({ capital, onUpdate, onClose }) => {
  const [form, setForm] = useState({ amount: "", date: "" });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (capital) {
      setForm({
        amount: capital.amount?.toString() || "",
        date: capital.date ? capital.date.split("T")[0] : "",
      });
      setErrors({});
      setIsSubmitting(false);
    }
  }, [capital]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("sw-TZ", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(amount || 0)) + " TZS";

  const handleSubmit = () => {
    const newErrors = {};
    if (!form.amount || parseFloat(form.amount) <= 0) {
      newErrors.amount = "Please enter a valid amount";
    }
    if (!form.date) {
      newErrors.date = "Please select a date";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toastError("Please fix all validation errors before continuing.");
      return;
    }

    setIsSubmitting(true);

    try {
      const result = onUpdate({
        capitalId: capital.capitalId,
        amount: parseFloat(form.amount),
        date: form.date,
      });

      if (result && typeof result.then === "function") {
        result
          .then(() => {
            toastSuccess(
              `Capital of ${formatCurrency(form.amount)} updated successfully!`,
            );
            setIsSubmitting(false);
            onClose();
          })
          .catch((error) => {
            toastError(
              `Failed to update capital: ${error.message || "Please try again."}`,
            );
            setIsSubmitting(false);
          });
      } else {
        toastSuccess(
          `Capital of ${formatCurrency(form.amount)} updated successfully!`,
        );
        setIsSubmitting(false);
        onClose();
      }
    } catch (error) {
      toastError(
        `Failed to update capital: ${error.message || "Please try again."}`,
      );
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Edit capital"
      maxWidth="sm:max-w-md"
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="gap-2 w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                Update capital
              </>
            )}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <FontAwesomeIcon icon={faEdit} className="text-blue-800 h-4 w-4" />
          </div>
          <div>
            <p className="text-sm text-gray-600 font-semibold">Edit capital</p>
            <p className="text-sm text-gray-600 font-medium">
              Update capital information
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faDollarSign}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Amount (TZS) <span className="text-red-700">*</span>
          </Label>
          <Input
            name="amount"
            type="number"
            min="0"
            step="0.01"
            value={form.amount}
            onChange={handleChange}
            placeholder="Enter capital amount"
            className={`h-10 text-sm ${errors.amount ? "border-red-500" : "border-gray-300"}`}
          />
          {errors.amount && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.amount}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faCalendarAlt}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Date <span className="text-red-700">*</span>
          </Label>
          <Input
            name="date"
            type="date"
            value={form.date}
            onChange={handleChange}
            className={`h-10 text-sm ${errors.date ? "border-red-500" : "border-gray-300"}`}
          />
          {errors.date && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.date}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default EditCapital;
